import { EyeIcon, PencilEdit02Icon, TrashIcon } from '@/icons';
import { Button, Space, type TableProps } from 'antd';
import { Link } from 'react-router';
import {
  useDeleteDesignationMutation,
  useGetAllDesignationsQuery,
} from '@/app/features/employee/emp-dashboard/emp-dashboard.api';
import ConfirmationModal from '@/components/ConfirmationModal';
import CustomTable from '@/components/CustomTable';
import StatusTag from '@/components/StatusTag';
import {
  DISCLOSER_KEYS,
  FILTER_KEYS,
} from '@/constants/keys.constants';
import { useAppDiscloser, useAppFilters } from '@/hooks';
import { getErrorMessage } from '@/lib/error';
import { toast } from '@/lib/toast';
import DesignationUsers from './_components/Designations/DesignationUsers';
import ManageDesignation from './_components/Designations/ManageDesignation';

const DESIGNATION_STATUS_CONFIG = [
  { label: 'Active', value: 'active', className: 'status status-success' },
  { label: 'Inactive', value: 'inactive', className: 'status status-error' },
];

const Designations = () => {
  // Use discloser hook for ManageDesignation drawer
  const [config, handleOpen, handleClose] = useAppDiscloser(
    DISCLOSER_KEYS.ORG_DESIGNATIONS,
  );

  // Use filters hook
  const { page, limit, filters, handlePagination } = useAppFilters(
    FILTER_KEYS.ORG_DESIGNATIONS,
  );

  // Fetch designations data
  const {
    data: designationsData,
    isLoading,
    isFetching,
  } = useGetAllDesignationsQuery({
    params: { page, limit, ...filters },
  });

  // Delete designation
  const [deleteDesignation, { isLoading: isDeleting }] =
    useDeleteDesignationMutation();

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    try {
      await deleteDesignation({ id: config?.data?.id }).unwrap();
      toast('success', 'Designation deleted successfully');
      handleClose();
    } catch (error: any) {
      const errorMessage = getErrorMessage(
        error,
        'Failed to delete designation',
      );
      toast('error', errorMessage);
    }
  };

  const columns: TableProps<any>['columns'] = [
    {
      title: 'Designation Name',
      dataIndex: 'name',
      render: (val: string) => val || 'n/a',
    },

    {
      title: 'Created Date',
      dataIndex: 'created_at',
      render: (val) => (val ? new Date(val).toLocaleDateString() : 'n/a'),
    },
    {
      title: 'Assigned Employees',
      dataIndex: 'total_users',
      render: (val) => val ?? 'n/a',
    },

    {
      title: 'Status',
      dataIndex: 'status',
      align: 'center',
      render: (status: string) => (
        <StatusTag value={status} config={DESIGNATION_STATUS_CONFIG} />
      ),
    },
    {
      align: 'end',
      title: 'Actions',
      render: (record) => (
        <Space>
          <Link to={``}>
            <Button
              size="small"
              className="action-btn"
              icon={<EyeIcon size={20} />}
              onClick={() => handleOpen({ type: 'VIEW', data: record })}
            />
          </Link>

          <Button
            size="small"
            className="action-btn"
            icon={<PencilEdit02Icon size={20} />}
            onClick={() => handleOpen({ type: 'UPDATE', data: record })}
          />

          <Button
            size="small"
            className="action-btn"
            icon={<TrashIcon size={20} />}
            onClick={() => handleOpen({ type: 'DELETE', data: record })}
          />
        </Space>
      ),
    },
  ];

  return (
    <div>
      <CustomTable
        page={page}
        limit={limit}
        hideRowSelect
        columns={columns}
        onPaginate={handlePagination}
        loading={isLoading || isFetching}
        tableRootClassName="border-0 p-0"
        dataSource={designationsData?.data?.data || []}
        total={designationsData?.data?.meta?.total || 0}
      />

      <ManageDesignation
        open={
          config.open && (config.type === 'ADD' || config.type === 'UPDATE')
        }
        id={config.data?.id}
        type={config.type}
        data={config.data}
        handleClose={handleClose}
      />

      <DesignationUsers
        open={config.open && config.type === 'VIEW'}
        data={config.data}
        handleClose={handleClose}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        loading={isDeleting}
        handleClose={handleClose}
        handleSubmit={handleDeleteConfirm}
        open={config.open && config.type === 'DELETE'}
        buttonName="Delete"
        title="Delete Designation"
        subTitle={`Are you sure you want to delete ${config?.data?.name}? This action cannot be undone.`}
      />
    </div>
  );
};

export default Designations;
